/**
 * Tenant slug resolution (client-side).
 *
 * Three forms are supported:
 *   1. Subdomain form:   https://{slug}.lanebooker.ca/            → slug = first label
 *                        http://{slug}.localhost:3000/             → same
 *   2. Path-prefix form: https://app.lanebooker.ca/t/{slug}/...   → slug from path
 *                        http://localhost:3000/t/{slug}/...        → same
 *   3. No slug at all:   http://localhost:3000/                    → returns null, caller
 *                        must either default to the user's sole membership or show
 *                        a picker.
 *
 * The resolver runs only in the browser; there is no SSR.
 */

/** Reserved subdomain labels that are NEVER tenants. */
const RESERVED_HOSTS = new Set([
  'app',
  'api',
  'widget',
  'www',
  'docs',
  'admin',
  'platform',
  'localhost',
]);

/** Tenant-known TLDs we recognize as "the platform". Everything else is treated
 *  as a custom domain and we fall back to path-based resolution. */
const PLATFORM_DOMAINS = ['lanebooker.ca'];

export interface ResolvedTenant {
  slug: string;
  source: 'subdomain' | 'path';
}

/** Returns `{ slug }` if this URL embeds a tenant slug, else null. */
export function resolveTenantSlug(
  location: { hostname: string; pathname: string } = window.location,
): ResolvedTenant | null {
  // 1. Path-prefix form wins when present — it's unambiguous even on subdomains.
  const pathMatch = location.pathname.match(/^\/t\/([a-z0-9-]+)(?:\/|$)/i);
  if (pathMatch) {
    return { slug: pathMatch[1].toLowerCase(), source: 'path' };
  }

  // 2. Subdomain form — only on known platform domains, or *.localhost for dev.
  const host = location.hostname.toLowerCase().replace(/:\d+$/, '');
  const labels = host.split('.');

  const isLocalhost = host.endsWith('localhost');
  const isPlatform = PLATFORM_DOMAINS.some((d) => host.endsWith(`.${d}`));

  if (isLocalhost && labels.length >= 2 && labels[0] !== 'localhost') {
    const first = labels[0];
    if (!RESERVED_HOSTS.has(first)) {
      return { slug: first, source: 'subdomain' };
    }
  }
  if (isPlatform && labels.length >= 3) {
    const first = labels[0];
    if (!RESERVED_HOSTS.has(first)) {
      return { slug: first, source: 'subdomain' };
    }
  }

  return null;
}

/** Strip the /t/{slug} prefix from a pathname, leaving the rest.
 *  Used when we need "the page within the tenant view". */
export function stripTenantPath(pathname: string): string {
  return pathname.replace(/^\/t\/[a-z0-9-]+(?:\/|$)/i, '/');
}
